import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import { generatePath, useNavigate, useSearchParams } from 'react-router-dom';

import { usePartnersReadEvents } from '@/api/hooks';
import { type PartnerEvent } from '@/api/routes/partners/partners.events.api';
import Button from '@/components/Button';
import LoadErrorState from '@/components/LoadErrorState';
import { IconPlus } from '@/components/icon/tabler/IconPlus';
import EventsEmptyState from '@/domain/events/components/EventsEmptyState';
import EventsListSkeleton from '@/domain/events/components/EventsListSkeleton';
import EventsStatusTabs from '@/domain/events/components/EventsStatusTabs';
import EventsTable from '@/domain/events/components/EventsTable';
import { type EventsTab } from '@/domain/events/types';
import { parseEventsTab, toEventsListParams } from '@/domain/events/utils';
import { useTranslate } from '@/state/i18n';
import { ScreenRoutes } from '@/types/router';
import { radiusTokens } from '@/theme/themePrimitives';

/** `/events` (P08): the partner's events by status. The tab and page live in the URL. */
const EventsList = () => {
  const t = useTranslate('domain.events.EventsList');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseEventsTab(searchParams.get('status'));
  const page = Math.max(0, Number(searchParams.get('page')) || 0);

  const params = useMemo(() => toEventsListParams(tab, page), [tab, page]);
  const { data, isLoading, isError, refetch } = usePartnersReadEvents(params);

  const changeTab = (next: EventsTab) =>
    setSearchParams(next === 'all' ? {} : { status: next }, { replace: true });
  const changePage = (next: number) =>
    setSearchParams({ ...(tab === 'all' ? {} : { status: tab }), page: String(next) });
  const create = () => navigate(ScreenRoutes.EVENT_NEW);
  const open = (event: PartnerEvent) =>
    navigate(generatePath(ScreenRoutes.EVENT_DETAIL, { eventId: event.id }));
  const edit = (event: PartnerEvent) =>
    navigate(generatePath(ScreenRoutes.EVENT_EDIT, { eventId: event.id }));

  const renderContent = () => {
    if (isLoading) return <EventsListSkeleton />;
    if (isError || !data) {
      return <LoadErrorState message={t('loadError')} onRetry={() => refetch()} />;
    }
    if (!data.items.length) {
      return <EventsEmptyState filtered={data.statusCounts.all > 0} onCreate={create} />;
    }
    return (
      <EventsTable
        events={data.items}
        count={data.count}
        page={page}
        onPageChange={changePage}
        onOpen={open}
        onEdit={edit}
      />
    );
  };

  return (
    <Stack gap={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
        <Typography component="h1" variant="h2">
          {t('title')}
        </Typography>
        <Button variant="contained" startIcon={<IconPlus />} onClick={create}>
          {t('create')}
        </Button>
      </Stack>
      <EventsStatusTabs value={tab} counts={data?.statusCounts} onChange={changeTab} />
      <Paper variant="outlined" sx={sx.surface}>
        {renderContent()}
      </Paper>
    </Stack>
  );
};

export default EventsList;

const sx = {
  surface: { borderRadius: radiusTokens.xxl, overflow: 'hidden' },
};
